import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { selectIsLoggedInUserAdmin } from '@core/store/auth/auth.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ConfigurationHttpClient } from 'demand-forecasting/services/http/configuration-http-client';
import { ForecastVersionActions } from 'demand-forecasting/store/forecast-version/forecast-version.actions';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  catchError,
  concatMap,
  exhaustMap,
  map,
  tap,
  withLatestFrom,
} from 'rxjs';
import { ConfigurationsActions } from './configurations.actions';
import { ConfigurationsSelectors } from './configurations.selectors';

@Injectable()
export class ConfigurationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly configurationHttpClient = inject(ConfigurationHttpClient);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  getAllConfigurations$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConfigurationsActions.getAllConfigurations),
        withLatestFrom(this.store.select(selectIsLoggedInUserAdmin)),
        tap(() =>
          this.store.dispatch(
            ConfigurationsActions.setGetAllConfigurationsIsFetching({
              isFetching: true,
            }),
          ),
        ),
        exhaustMap(([, isLoggedInUserAdmin]) => {
          const apiCall = isLoggedInUserAdmin
            ? this.configurationHttpClient.getConfigurations()
            : this.configurationHttpClient.getVisibleConfigurations();
          return apiCall.pipe(
            map((configurations) => {
              this.store.dispatch(
                ConfigurationsActions.setAllConfigurations({
                  configurations,
                }),
              );
              this.store.dispatch(
                ForecastVersionActions.getAllForecastVersionsByIds({
                  forecastVersionIds: configurations
                    .map((conf) => conf.latestVersionId)
                    .flatMap((val) => (val ? [val] : [])),
                }),
              );
            }),
            catchError((error: HttpErrorResponse) => {
              Sentry.captureException(error, {
                tags: {
                  feature: 'configurations',
                  operation: 'get-all-configurations',
                },
                extra: {
                  statusCode: error.status,
                  statusText: error.statusText,
                  errorMessage: error.message,
                },
              });
              return EMPTY;
            }),
          );
        }),
        tap(() =>
          this.store.dispatch(
            ConfigurationsActions.setGetAllConfigurationsIsFetching({
              isFetching: false,
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  getConfiguration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConfigurationsActions.getConfiguration),
        withLatestFrom(
          this.store.select(
            ConfigurationsSelectors.selectAreConfigurationsFetching,
          ),
          this.store.select(ConfigurationsSelectors.selectAllConfigurations),
        ),
        exhaustMap(([{ configurationId }, , configurations]) =>
          this.configurationHttpClient.getConfiguration(configurationId).pipe(
            map((configuration) => {
              const doesConfigurationExistInStore = configurations.some(
                (v) => v.id === configurationId,
              );
              if (doesConfigurationExistInStore) {
                this.store.dispatch(
                  ConfigurationsActions.patchConfiguration({ configuration }),
                );
              } else {
                this.store.dispatch(
                  ConfigurationsActions.addConfiguration({
                    configuration,
                  }),
                );
              }
            }),
            catchError((error: HttpErrorResponse) => {
              Sentry.captureException(error, {
                tags: {
                  feature: 'configurations',
                  operation: 'get-configuration',
                },
                extra: {
                  configurationId,
                  statusCode: error.status,
                  statusText: error.statusText,
                  errorMessage: error.message,
                },
              });
              return EMPTY;
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  createConfiguration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConfigurationsActions.createConfiguration),
        tap(() =>
          this.store.dispatch(
            ConfigurationsActions.setCreateConfigurationIsPending({
              isPending: true,
            }),
          ),
        ),
        exhaustMap(({ createConfigurationDto, executeConfigurationDto }) =>
          this.configurationHttpClient
            .createConfiguration(createConfigurationDto)
            .pipe(
              map((res) => {
                this.toastService.success(
                  this.translateService.instant(
                    'UTILITY.NEW_CONFIGURATION_CREATED_SUCCESSFULLY',
                  ),
                );
                this.store.dispatch(
                  ConfigurationsActions.addConfiguration({
                    configuration: res,
                  }),
                );
                this.store.dispatch(
                  ForecastVersionActions.createVersion({
                    configurationId: res.id,
                    executeConfigurationDto,
                  }),
                );
              }),
              catchError((error: HttpErrorResponse) => {
                Sentry.captureException(error, {
                  tags: {
                    feature: 'configurations',
                    operation: 'create-configuration',
                  },
                  extra: {
                    statusCode: error.status,
                    statusText: error.statusText,
                    errorMessage: error.message,
                  },
                });
                this.toastService.error(
                  this.translateService.instant(
                    'UTILITY.CREATE_CONFIGURATION_FAILED',
                  ),
                );
                return EMPTY;
              }),
            ),
        ),
        tap(() =>
          this.store.dispatch(
            ConfigurationsActions.setCreateConfigurationIsPending({
              isPending: false,
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  updateConfiguration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConfigurationsActions.updateConfiguration),
        tap(() =>
          this.store.dispatch(
            ConfigurationsActions.setCreateConfigurationIsPending({
              isPending: true,
            }),
          ),
        ),
        exhaustMap(({ configurationId, updateConfigurationData }) =>
          this.configurationHttpClient
            .updateConfiguration(configurationId, updateConfigurationData)
            .pipe(
              map(() => {
                this.toastService.success(
                  this.translateService.instant(
                    'RESULT_OVERVIEW.UPDATE_CONFIGURATION.SUCCESS.BODY',
                  ),
                  this.translateService.instant(
                    'RESULT_OVERVIEW.UPDATE_CONFIGURATION.SUCCESS.TITLE',
                  ),
                );
                // TODO optimize with PM-2127
                this.store.dispatch(
                  ConfigurationsActions.getAllConfigurations(),
                );
              }),
              catchError((error: HttpErrorResponse) => {
                Sentry.captureException(error, {
                  tags: {
                    feature: 'configurations',
                    operation: 'update-configuration',
                  },
                  extra: {
                    configurationId,
                    statusCode: error.status,
                    statusText: error.statusText,
                    errorMessage: error.message,
                  },
                });
                this.toastService.error(
                  this.translateService.instant(
                    'RESULT_OVERVIEW.UPDATE_CONFIGURATION.ERROR.BODY',
                  ),
                  this.translateService.instant(
                    'RESULT_OVERVIEW.UPDATE_CONFIGURATION.ERROR.TITLE',
                  ),
                );
                return EMPTY;
              }),
            ),
        ),
        tap(() =>
          this.store.dispatch(
            ConfigurationsActions.setCreateConfigurationIsPending({
              isPending: false,
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );

  toggleReplenishmentConfiguration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ConfigurationsActions.toggleReplenishmentConfiguration),
        tap(() =>
          this.store.dispatch(
            ConfigurationsActions.setCreateConfigurationIsPending({
              isPending: true,
            }),
          ),
        ),
        exhaustMap(
          ({
            currentConfigurationId,
            newConfigurationId,
            currentConfigurationData,
            newConfigurationData,
          }) =>
            this.configurationHttpClient
              .updateConfiguration(
                currentConfigurationId,
                currentConfigurationData,
              )
              .pipe(
                concatMap(() =>
                  this.configurationHttpClient.updateConfiguration(
                    newConfigurationId,
                    newConfigurationData,
                  ),
                ),
                map(() => {
                  this.toastService.success(
                    this.translateService.instant(
                      'RESULT_OVERVIEW.UPDATE_CONFIGURATION.SUCCESS.BODY',
                    ),
                    this.translateService.instant(
                      'RESULT_OVERVIEW.UPDATE_CONFIGURATION.SUCCESS.TITLE',
                    ),
                  );
                  this.store.dispatch(
                    ConfigurationsActions.getAllConfigurations(),
                  );
                }),
                catchError((error: HttpErrorResponse) => {
                  Sentry.captureException(error, {
                    tags: {
                      feature: 'configurations',
                      operation: 'toggle-replenishment-configuration',
                    },
                    extra: {
                      currentConfigurationId,
                      newConfigurationId,
                      statusCode: error.status,
                      statusText: error.statusText,
                      errorMessage: error.message,
                    },
                  });
                  this.toastService.error(
                    this.translateService.instant(
                      'RESULT_OVERVIEW.UPDATE_CONFIGURATION.ERROR.BODY',
                    ),
                    this.translateService.instant(
                      'RESULT_OVERVIEW.UPDATE_CONFIGURATION.ERROR.TITLE',
                    ),
                  );
                  return EMPTY;
                }),
              ),
        ),
        tap(() =>
          this.store.dispatch(
            ConfigurationsActions.setCreateConfigurationIsPending({
              isPending: false,
            }),
          ),
        ),
      ),
    {
      dispatch: false,
    },
  );
}
